import { Controller } from "stimulus";

export default class BeeController extends Controller {

    static targets = ["container", "modal"]

    connect() {
        this.beeInstance = null;
        this.beeDefaultTemplate = null;
        this.editingNode = null;
        this.beeCreating = false;
        this.pendingNode = null;

        // Boot the (heavy) BEE editor iframe and fetch the starter template
        // ahead of time so the first "Design" click renders instantly instead
        // of waiting on a serial token-fetch -> editor-boot -> template-fetch
        // chain. Defer to idle time so it doesn't compete with initial render.
        const warm = () => {
            this.prefetchDefaultTemplate();
            this.prewarmEditor();
        };
        if ('requestIdleCallback' in window) {
            requestIdleCallback(warm, { timeout: 2000 });
        } else {
            setTimeout(warm, 800);
        }
    }

    open(e) {
        e.stopPropagation();
        const node = e.currentTarget.closest("[id^='node-']");
        if (!node) return;

        this.editingNode = node;
        this.showModal();

        if (this.beeInstance) {
            this.startWithNodeData(this.beeInstance, node);
        } else if (this.beeCreating) {
            // Pre-warm is still booting the editor; hydrate the moment it's ready.
            this.pendingNode = node;
        } else {
            this.initEditor(node);
        }
    }

    close(e) {
        if (e) e.stopPropagation();
        this.hideModal();
    }

    save(e) {
        e.stopPropagation();
        if (this.beeInstance) {
            this.beeInstance.save();
        }
    }

    // Private

    showModal() {
        const modal = document.getElementById('email-design-modal');
        if (!modal) return;
        // Visibility here is driven by the `is-hidden` class, but the shared
        // sequence-controller `closeModal()` blanket-stamps an inline
        // `display: none` on every `.modal` (e.g. after applying a trigger
        // filter). That inline style outranks the CSS and would keep this
        // modal hidden even after we drop `is-hidden`, so clear it first.
        modal.style.display = '';
        modal.classList.remove('is-hidden');
    }

    hideModal() {
        document.getElementById('email-design-modal')?.classList.add('is-hidden');
    }

    prewarmEditor() {
        // BeePlugin loads from a CDN <script> in the layout head; if it isn't
        // ready yet, the lazy initEditor path on click will handle creation.
        if (typeof BeePlugin === 'undefined') return;
        this.createInstance();
    }

    prefetchDefaultTemplate() {
        const container = document.getElementById('campaign-bee-container');
        const defaultUrl = container?.dataset.defaultTemplateUrl;
        if (!defaultUrl || this.beeDefaultTemplate) return;
        const controller = this;
        $.getJSON(defaultUrl, function(template) {
            controller.beeDefaultTemplate = template;
        });
    }

    initEditor(node) {
        this.pendingNode = node;
        this.createInstance();
    }

    createInstance() {
        if (this.beeInstance || this.beeCreating) return;
        const container = document.getElementById('campaign-bee-container');
        if (!container) return;
        const uid = container.dataset.uid;
        const tokenUrl = container.dataset.tokenUrl;
        const controller = this;

        this.beeCreating = true;
        $.get(tokenUrl, { uid: uid }, function(token) {
            BeePlugin.create(token, controller.beeConfig(uid), function(bee) {
                controller.beeInstance = bee;
                controller.beeCreating = false;
                // If the user already clicked "Design" while we were booting,
                // hydrate the node they're waiting on now.
                if (controller.pendingNode) {
                    const node = controller.pendingNode;
                    controller.pendingNode = null;
                    controller.startWithNodeData(bee, node);
                }
            });
        }).fail(function() {
            controller.beeCreating = false;
        });
    }

    beeConfig(uid) {
        const controller = this;
        return {
            uid: uid,
            container: 'campaign-bee-container',
            autosave: false,
            language: 'en-US',
            preventClose: false,
            mergeTags: [
                { name: 'First Name', value: '[first-name | insert fallback]' },
                { name: 'Full Name', value: '[full-name | insert fallback]' },
                { name: 'Birthday', value: '[birthday | insert fallback]' },
                { name: 'Days Since Last Visit', value: '[days-since-last-visit | insert fallback]' },
                { name: 'Last Visit Date', value: '[last-visit-date | insert fallback]' },
                { name: 'Business name', value: '[business-name | insert fallback]' },
                { name: 'Contact URL', value: '[contact-us-url | insert fallback]' }
            ],
            onSave: function(jsonFile, htmlFile) {
                controller.onSaveComplete(jsonFile, htmlFile);
            },
            onError: function(errorMessages) {
                console.error('BEE error', errorMessages);
            }
        };
    }

    startWithNodeData(bee, node) {
        // Priority order: a previously-saved custom design wins because it
        // represents user edits. Otherwise, if the user picked a template on
        // this node, we hydrate BEE with that template's JSON so they can
        // tweak it instead of starting blank. Falls back to the default BEE
        // starter template when neither is present.
        const existingJson = node.querySelector('.email-json')?.value;
        if (existingJson) {
            try {
                bee.start(JSON.parse(existingJson));
                return;
            } catch(err) { /* fall through to template/default */ }
        }

        const templateId = node.querySelector('.email-template-id')?.value;
        if (templateId) {
            this.loadSelectedTemplate(bee, templateId);
            return;
        }

        this.loadDefaultTemplate(bee);
    }

    loadSelectedTemplate(bee, templateId) {
        const container = document.getElementById('campaign-bee-container');
        const urlPattern = container?.dataset.templateUrl;
        if (!urlPattern) {
            this.loadDefaultTemplate(bee);
            return;
        }

        const url = urlPattern.replace('__TEMPLATE_ID__', encodeURIComponent(templateId));
        const controller = this;
        $.getJSON(url, function(template) {
            if (template && Object.keys(template).length > 0) {
                bee.start(template);
            } else {
                controller.loadDefaultTemplate(bee);
            }
        }).fail(function() {
            controller.loadDefaultTemplate(bee);
        });
    }

    loadDefaultTemplate(bee) {
        const controller = this;
        if (this.beeDefaultTemplate) {
            bee.start(this.beeDefaultTemplate);
            return;
        }

        // Match the blasts step-3 flow: the default is the server-rendered,
        // branded starter design (logo, business name, brand colors) rather
        // than BEE's generic catalog template.
        const container = document.getElementById('campaign-bee-container');
        const defaultUrl = container?.dataset.defaultTemplateUrl;
        if (!defaultUrl) {
            bee.start({ page: { rows: [] } });
            return;
        }

        $.getJSON(defaultUrl, function(template) {
            controller.beeDefaultTemplate = template;
            bee.start(template);
        }).fail(function() {
            bee.start({ page: { rows: [] } });
        });
    }

    onSaveComplete(jsonFile, htmlFile) {
        const node = this.editingNode;
        if (!node) return;

        node.querySelector('.email-json').value = jsonFile;
        node.querySelector('.email-html').value = htmlFile;
        node.querySelector('.email-template-id').value = '';
        const contentHidden = node.querySelector('.email-content');
        if (contentHidden) contentHidden.value = 'custom';

        const preview = node.querySelector('.email-body-preview');
        preview.textContent = 'Custom email designed';
        preview.classList.add('has-content');

        // The inline unselect affordance is template-specific; a custom
        // design replaces the template selection (we just nulled
        // `.email-template-id`), so the button should retract too.
        const unselectBtn = node.querySelector('.email-unselect-btn');
        if (unselectBtn) unselectBtn.style.display = 'none';

        this.close();
        this.notifySequenceUpdate();
        // The freshly-saved HTML now has a body to score against; ask the
        // per-node spam-score controller to re-validate. Mirrors the same
        // event that `selectEmailTemplate` dispatches in
        // sequence_controller.js.
        node.dispatchEvent(new CustomEvent('spam-score:revalidate', { bubbles: false }));
        toastr.success('Email design saved.');
    }

    notifySequenceUpdate() {
        const seqEl = document.getElementById('form-container');
        if (!seqEl) return;
        const seqController = this.application.getControllerForElementAndIdentifier(
            seqEl, "mission-control--campaign--sequence"
        );
        if (seqController) seqController.updateSequenceJson();
    }
}
